/**
 * Phase 107 (Plan 107-07 / SLA-PRICE-03, SLA-PRICE-08) — TanStack Query hooks
 * for the SLA pricing configurator, wrapping `services/api/slaPricing.ts`.
 *
 * Follows `useLeads.ts`'s shape: `useQuery` with a stable `queryKey`,
 * `useMutation` with `onSuccess` invalidation and `useErrorToast()` on
 * `onError`.
 *
 * Two persistence granularities (107-UI-SPEC.md Implementation Note 1):
 * - Band mutations (`useCreateBand`/`useUpdateBand`/`useDeleteBand`) are
 *   immediate-save — each invalidates both the configuration detail and the
 *   list, because a band's rate changes the monthly price shown in both
 *   places.
 * - `useSaveCells` is the batch path — one mutation carrying the full
 *   desired cell set, invalidating the same two keys on success. No
 *   optimistic pre-mutation cache patching (T-107-AUTOSAVE / D-10): an
 *   autosave that clobbers a saved named variant mid-experiment would be
 *   actively harmful.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useErrorToast } from './queryUtils';
import {
  fetchConfigurations,
  fetchConfiguration,
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
  duplicateConfiguration,
  createBand,
  updateBand,
  deleteBand,
  saveCells,
  type CreateConfigurationInput,
  type UpdateConfigurationInput,
  type CreateBandInput,
  type UpdateBandInput,
  type SlaPricingCell,
} from '../services/api/slaPricing';

export function useConfigurations() {
  return useQuery({
    queryKey: ['slaPricingConfigurations'],
    queryFn: fetchConfigurations,
  });
}

export function useConfiguration(id: number) {
  return useQuery({
    queryKey: ['slaPricingConfiguration', id],
    queryFn: () => fetchConfiguration(id),
    enabled: Number.isFinite(id),
  });
}

export function useCreateConfiguration() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (input: CreateConfigurationInput) => createConfiguration(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
    },
    onError: (err) => onError(err, "Couldn't create the configuration"),
  });
}

export function useUpdateConfiguration() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateConfigurationInput }) =>
      updateConfiguration(id, input),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfiguration', id] });
    },
    onError: (err) => onError(err, "Couldn't update the configuration"),
  });
}

export function useDeleteConfiguration() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => deleteConfiguration(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
      queryClient.removeQueries({ queryKey: ['slaPricingConfiguration', id] });
    },
    onError: (err) => onError(err, "Couldn't delete the configuration"),
  });
}

export function useDuplicateConfiguration() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: (id: number) => duplicateConfiguration(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfiguration', data.data.id] });
    },
    onError: (err) => onError(err, "Couldn't duplicate the configuration"),
  });
}

export function useCreateBand() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({
      configurationId,
      input,
    }: {
      configurationId: number;
      input: CreateBandInput;
    }) => createBand(configurationId, input),
    onSuccess: (_data, { configurationId }) => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfiguration', configurationId] });
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
    },
    onError: (err) => onError(err, "Couldn't add the band"),
  });
}

export function useUpdateBand() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({
      configurationId,
      bandId,
      input,
    }: {
      configurationId: number;
      bandId: number;
      input: UpdateBandInput;
    }) => updateBand(configurationId, bandId, input),
    onSuccess: (_data, { configurationId }) => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfiguration', configurationId] });
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
    },
    onError: (err) => onError(err, "Couldn't update the band"),
  });
}

export function useDeleteBand() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({ configurationId, bandId }: { configurationId: number; bandId: number }) =>
      deleteBand(configurationId, bandId),
    onSuccess: (_data, { configurationId }) => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfiguration', configurationId] });
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
    },
    onError: (err) => onError(err, "Couldn't delete the band"),
  });
}

export function useSaveCells() {
  const queryClient = useQueryClient();
  const onError = useErrorToast();
  return useMutation({
    mutationFn: ({
      configurationId,
      cells,
    }: {
      configurationId: number;
      cells: SlaPricingCell[];
    }) => saveCells(configurationId, cells),
    onSuccess: (_data, { configurationId }) => {
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfiguration', configurationId] });
      queryClient.invalidateQueries({ queryKey: ['slaPricingConfigurations'] });
    },
    onError: (err) => onError(err, "Couldn't save the grid"),
  });
}
